import {resolveAssetTitle} from './asset-fields';
import {hasAssetTypeName} from './asset-type.registry';

import type {AssetFieldKey, AssetTitleSource} from './asset-type.descriptor';
import type {AssetType} from '@sollapay/enums';
import type {AssetSummaryDTO} from '@sollapay/types';

/** Resolves copy for a display name. i18n-free package — the caller owns the actual strings. */
export interface AssetDisplayNameCopy {
  typeName: (assetType: AssetType) => string;
  title: (source: AssetTitleSource, values: string[]) => string;
}

export interface ResolvedAssetDisplayName {
  typeName: string;
  title: string;
  /** The fields the title consumed, so a caller can drop them from whatever it renders next. */
  consumed: AssetFieldKey[];
}

/** The short-name chokepoint — titles on `resolveAssetTitle`, never lists every filled field. */
export function resolveAssetDisplayName(
  asset: AssetSummaryDTO,
  copy: AssetDisplayNameCopy
): ResolvedAssetDisplayName {
  const typeName = hasAssetTypeName(asset.assetType) ? copy.typeName(asset.assetType).trim() : '';
  const resolved = resolveAssetTitle(asset.assetType, asset);

  return {
    typeName,
    title: resolved ? copy.title(resolved.source, resolved.values).trim() : '',
    consumed: resolved?.consumed ?? []
  };
}

/** Single joined-string asset name; falls back to displayCode when neither type nor title resolves. */
export function formatAssetDisplayName(asset: AssetSummaryDTO, copy: AssetDisplayNameCopy): string {
  const {typeName, title} = resolveAssetDisplayName(asset, copy);
  return [typeName, title].filter(Boolean).join(' · ') || asset.displayCode;
}
